import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { FC } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../../i18n/I18nContext';
import { apiClient } from '../../utils/api-client';
import Layout from '../../components/Layout';
import ChunkRenderer from './ChunkRenderer';
import ArticleMetadata from './ArticleMetadata';
import { useAudioPlayer } from './useAudioPlayer';
import { useVisibleChunk } from './useVisibleChunk';
import { useSettings } from '../../contexts/SettingsContext';
import { useToast } from '../../contexts/ToastContext';
import { useAudioState } from '../../contexts/AudioStateContext';
import { useBackgroundLoader } from '../../contexts/BackgroundLoaderContext';
import type { ArticleHead, ArticleBundle } from './types';
import { translationService } from './translation-service';
import { SelectionHistoryProvider, useSelectionHistory } from './SelectionHistoryContext';
import ConfirmDialog from '../../components/ConfirmDialog';
import { IconButton } from '../../components/ui/icon-button';
import { FloatingPanel } from '../../components/ui/floating-panel';
import { getCachedArticleAssets, setCachedArticleAssets } from './asset-cache';
import { LoadingBar } from '../../components/ui/loading-bar';
import PhraseExportButton from './PhraseExportButton';

const ArticleBody: FC = () => {
  const { t } = useTranslation();
  const { articleId } = useParams<{ articleId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { articles } = useBackgroundLoader();
  const initialArticleHead = (location.state as { article?: ArticleHead })?.article;
  const contextArticle = articles.find(a => a.articleId === articleId);
  const articleHead = contextArticle ?? initialArticleHead;
  const [bundle, setBundle] = useState<ArticleBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const chunks = bundle?.chunks ?? [];
  const sourceLanguage = articleHead?.lang;
  const { settings } = useSettings();
  const { showToast } = useToast();
  const { setAudioState } = useAudioState();

  const { getFocusedChunkIndex, focusedChunkIndex, setChunkRef, scrollToChunk } = useVisibleChunk();

  // Each chunk's audio lives as a standalone MP3 in S3, served by the
  // articles-audio handler. The bundle carries a relative path per chunk
  // (`audioUrl`) which we prepend with the API base to make it absolute.
  // Chunks without audio (e.g. same-language short-circuit) produce null
  // and useAudioPlayer silently skips them.
  const apiBaseUrl = import.meta.env.VITE_API_URL;
  const chunkAudios = useMemo(
    () => chunks.map((c) =>
      c.audioUrl ? { url: `${apiBaseUrl}${c.audioUrl}` } : null
    ),
    [chunks, apiBaseUrl]
  );

  const handleAudioError = useCallback(
    (message: string) => { showToast(message, 'error'); },
    [showToast]
  );

  const handleActiveChunkChange = useCallback(
    (_chunkIndex: number | null) => {},
    []
  );

  const {
    isPlaying,
    activeChunkIndex,
    pause,
    stop,
    playFromHere,
    getCurrentTime,
  } = useAudioPlayer({
    chunkAudios,
    speed: settings.audioSpeed,
    onError: handleAudioError,
    onActiveChunkChange: handleActiveChunkChange,
    scrollToChunk,
    getFocusedChunkIndex,
  });

  // Same-language short-circuit and per-chunk failures can leave a bundle
  // with no playable audio; hide playback affordances in that case.
  const hasAudio = chunkAudios.some((a) => a !== null);

  // Sync global audio state
  useEffect(() => {
    setAudioState(isPlaying, articleId ?? null);
  }, [isPlaying, articleId, setAudioState]);

  // Media-session metadata is intentionally NOT set. Playback is
  // per-chunk — each chunk is a separate audio object — so the OS
  // panel would tear down and rebuild between every chunk as Chrome
  // rebinds to the new element. Fixing it properly would mean
  // concatenating chunks into one article-level MP3 and threading
  // absolute offsets through the speechmark highlighter, which is a
  // multi-day refactor for a feature no user has asked for. Revisit
  // if beta feedback actually flags lock-screen listening.

  // Stop audio on unmount
  useEffect(() => {
    return () => {
      setAudioState(false, null);
      stop();
    };
  }, []);

  // Suppress native text selection and context menu on article body
  const articleRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = articleRef.current;
    if (!el) return;
    const prevent = (e: Event) => e.preventDefault();
    el.addEventListener('selectstart', prevent);
    el.addEventListener('contextmenu', prevent);
    return () => {
      el.removeEventListener('selectstart', prevent);
      el.removeEventListener('contextmenu', prevent);
    };
  }, []);

  // Load article bundle on mount — one fetch, everything included
  useEffect(() => {
    if (!articleId) return;

    const loadArticle = async () => {
      try {
        // Try IndexedDB cache first
        let articleBundle = await getCachedArticleAssets(articleId);
        if (!articleBundle) {
          articleBundle = await apiClient.get<ArticleBundle>(
            `/reader/articles/${articleId}/assets`
          );
          await setCachedArticleAssets(articleId, articleBundle);
        }

        if (!articleBundle?.chunks?.length) {
          setError(t('articles.load_article_error'));
          setLoading(false);
          return;
        }

        setBundle(articleBundle);
        setError(null);
      } catch (err) {
        console.error('Failed to load article:', err);
        setError(t('articles.load_article_error'));
      } finally {
        setLoading(false);
      }
    };

    loadArticle();
  }, [articleId]);

  // Pick up server-side grammar enrichment that may have landed since the
  // initial load — enrich-article-assets runs async after orchestrate-*
  // writes the bundle, so re-fetch the bundle from S3 whenever the user
  // returns to the tab.
  useEffect(() => {
    if (!articleId) return;
    const onVisible = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const fresh = await apiClient.get<ArticleBundle>(
          `/reader/articles/${articleId}/assets`,
        );
        if (fresh?.chunks?.length) {
          await setCachedArticleAssets(articleId, fresh);
          setBundle(fresh);
        }
      } catch (err) {
        console.warn('Bundle refresh failed:', err);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [articleId]);

  // Clear pending translation requests when target language changes
  useEffect(() => {
    translationService.onLanguageChange();
  }, [settings.language]);

  return (
    <Layout title="">
      {loading ? (
        <LoadingBar visible />
      ) : error || articleHead?.processingStatus === 'error' ? (
        <div className="bg-destructive text-destructive-foreground rounded-md p-6 text-center">
          <p className="mb-4">{error || t('articles.creation_error')}</p>
          <div className="flex justify-center gap-3">
            <button
              onClick={() => navigate('/articles')}
              className="px-4 py-2 text-sm rounded border border-border text-foreground hover:bg-muted transition-colors"
            >
              {t('articles.back_to_articles')}
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 text-sm rounded bg-primary text-primary-foreground hover:opacity-90 transition-colors"
            >
              {t('common.retry')}
            </button>
          </div>
        </div>
      ) : (
        <SelectionHistoryProvider articleId={articleId!}>
          <article ref={articleRef} className="pb-8">
            {articleHead && (
              <ArticleMetadata article={articleHead} />
            )}

            {chunks.map((chunk, index) => (
              <div
                key={`${index}-${settings.language}`}
                data-chunk-index={index}
                ref={(el) => setChunkRef(index, el)}
                className="relative"
                style={{ scrollMarginTop: '0' }}
              >
                {focusedChunkIndex === index && (
                  <div
                    aria-hidden="true"
                    className="absolute top-0 bottom-0 w-[5px] rounded bg-primary"
                    style={{ left: '-12px' }}
                  />
                )}
                <ChunkRenderer
                  articleId={articleId!}
                  chunkIndex={index}
                  chunk={chunk}
                  sourceLanguage={sourceLanguage}
                  targetLanguage={settings.language}
                  isActiveChunk={activeChunkIndex === index}
                  isAudioPlaying={isPlaying}
                  getCurrentTime={getCurrentTime}
                />
              </div>
            ))}
          </article>

          {/* Floating side panel — phrase export (top) + show/hide + clear (bottom) */}
          <SideControls articleId={articleId!} />

          {/* Floating playback button \u2014 one tap plays from the focused chunk */}
          {hasAudio && (
            <FloatingPanel side="end" position="bottom">
              {isPlaying ? (
                <button
                  onClick={pause}
                  className="bg-destructive text-destructive-foreground rounded-xl w-12 h-12 flex items-center justify-center shadow-lg hover:opacity-90 transition-opacity"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="6" y="5" width="4" height="14" rx="1" />
                    <rect x="14" y="5" width="4" height="14" rx="1" />
                  </svg>
                </button>
              ) : (
                <button
                  onClick={() => playFromHere(getFocusedChunkIndex())}
                  className="bg-success text-success-foreground rounded-xl w-12 h-12 flex items-center justify-center shadow-lg hover:opacity-90 transition-opacity"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </button>
              )}
            </FloatingPanel>
          )}
        </SelectionHistoryProvider>
      )}
    </Layout>
  );
};

/**
 * Right-edge floating panel hosting selection show/hide, the phrase-export
 * copy button, and selection clear (top → bottom). Sharing one
 * FloatingPanel keeps the vertical positioning logic in one place.
 */
interface SideControlsProps {
  articleId: string;
}

const SideControls: FC<SideControlsProps> = ({ articleId }) => {
  const { t } = useTranslation();
  const { clear, hasSelections, hidden, toggleHidden } = useSelectionHistory();
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  return (
    <>
      <FloatingPanel side="end">
        <IconButton
          onClick={toggleHidden}
          disabled={!hasSelections}
          title={hidden ? t('selections.show') : t('selections.hide')}
        >
          {hidden ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" /><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" /><path d="M1 1l22 22" /><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
            </svg>
          )}
        </IconButton>
        <PhraseExportButton articleId={articleId} />
        <IconButton onClick={() => setShowClearConfirm(true)} disabled={!hasSelections} title={t('selections.clear')}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M5 6v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V6" /><path d="M10 11v6" /><path d="M14 11v6" />
          </svg>
        </IconButton>
      </FloatingPanel>
      <ConfirmDialog
        open={showClearConfirm}
        title={t('selections.clear_title')}
        message={t('selections.clear_message')}
        confirmLabel={t('selections.clear')}
        destructive
        onConfirm={() => {
          setShowClearConfirm(false);
          clear();
        }}
        onCancel={() => setShowClearConfirm(false)}
      />
    </>
  );
};

export default ArticleBody;
